import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/config';

export default function DeviceRegistrations() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [approveDialog, setApproveDialog] = useState({ open: false, registration: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, registration: null, reason: '' });
  const [editDialog, setEditDialog] = useState({ open: false, registration: null, description: '' });

  useEffect(() => {
    fetchRegistrations();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchRegistrations, 10000);
    return () => clearInterval(interval);
  }, [filterStatus]);

  const fetchRegistrations = async () => {
    try {
      setError(null);
      const params = filterStatus ? { status: filterStatus } : {};
      const response = await axios.get(`${API_BASE_URL}/api/admin/device-registrations`, { params });

      // Ensure we always have an array
      const data = Array.isArray(response.data) ? response.data : [];
      setRegistrations(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      const errorMsg = error.response?.status === 404
        ? 'Backend endpoint not found. Please deploy the backend code.'
        : `Failed to load registrations: ${error.message}`;
      setError(errorMsg);
      // Set empty array on error to prevent filter errors
      setRegistrations([]);
      setLoading(false);
    }
  };

  const handleApprove = async (registration) => {
    setApproveDialog({ open: true, registration });
  };

  const confirmApprove = async () => {
    try {
      const { registration } = approveDialog;
      await axios.post(`${API_BASE_URL}/api/admin/device-registrations/${registration._id}/approve`, {
        approvedBy: 'admin',
        description: registration.requestedDescription || `${registration.deviceInfo?.manufacturer} ${registration.deviceInfo?.model}`
      });

      alert(`✅ Device approved successfully!\n\nDevice ID: ${registration.deviceId}\nThe device will receive its token within 30 seconds.`);
      setApproveDialog({ open: false, registration: null });
      fetchRegistrations();
    } catch (error) {
      console.error('Error approving registration:', error);
      alert('Failed to approve device registration');
    }
  };

  const handleReject = async (registration) => {
    setRejectDialog({ open: true, registration, reason: '' });
  };

  const confirmReject = async () => {
    try {
      const { registration, reason } = rejectDialog;
      await axios.post(`${API_BASE_URL}/api/admin/device-registrations/${registration._id}/reject`, {
        rejectedBy: 'admin',
        reason: reason || 'No reason provided'
      });

      alert(`❌ Device rejected.\n\nDevice ID: ${registration.deviceId}\nReason: ${reason || 'No reason provided'}`);
      setRejectDialog({ open: false, registration: null, reason: '' });
      fetchRegistrations();
    } catch (error) {
      console.error('Error rejecting registration:', error);
      alert('Failed to reject device registration');
    }
  };

  const handleEdit = async (registration) => {
    setEditDialog({
      open: true,
      registration,
      description: registration.requestedDescription || ''
    });
  };

  const confirmEdit = async () => {
    try {
      const { registration, description } = editDialog;

      if (!description || description.trim() === '') {
        alert('Description cannot be empty');
        return;
      }

      await axios.patch(`${API_BASE_URL}/api/admin/device-registrations/${registration._id}/description`, {
        description: description.trim()
      });

      alert('✅ Device label updated successfully!');
      setEditDialog({ open: false, registration: null, description: '' });
      fetchRegistrations();
    } catch (error) {
      console.error('Error updating description:', error);
      alert(`Failed to update description: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDelete = async (registrationId) => {
    if (!window.confirm('Are you sure you want to delete this registration request?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/admin/device-registrations/${registrationId}`);
      alert('Registration request deleted');
      fetchRegistrations();
    } catch (error) {
      console.error('Error deleting registration:', error);
      alert('Failed to delete registration request');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { backgroundColor: '#ffc107', color: '#000' },
      approved: { backgroundColor: '#28a745', color: '#fff' },
      rejected: { backgroundColor: '#dc3545', color: '#fff' }
    };

    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        ...styles[status]
      }}>
        {status.toUpperCase()}
      </span>
    );
  };

  const pendingCount = registrations.filter(r => r.status === 'pending').length;

  return (
    <div style={{ padding: '20px', backgroundColor: '#000', minHeight: '100vh' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 8px 0', color: '#fff' }}>Device Registration Requests</h2>
        <p style={{ margin: 0, color: '#999' }}>Approve or reject devices requesting access to the fleet</p>
      </div>

      {/* Filter Tabs */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <button
          onClick={() => setFilterStatus('pending')}
          style={{
            padding: '8px 16px',
            border: filterStatus === 'pending' ? '2px solid #007bff' : '1px solid #ccc',
            backgroundColor: filterStatus === 'pending' ? '#007bff' : '#fff',
            color: filterStatus === 'pending' ? '#fff' : '#000',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: filterStatus === 'pending' ? 'bold' : 'normal'
          }}
        >
          Pending {pendingCount > 0 && `(${pendingCount})`}
        </button>
        <button
          onClick={() => setFilterStatus('approved')}
          style={{
            padding: '8px 16px',
            border: filterStatus === 'approved' ? '2px solid #28a745' : '1px solid #ccc',
            backgroundColor: filterStatus === 'approved' ? '#28a745' : '#fff',
            color: filterStatus === 'approved' ? '#fff' : '#000',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: filterStatus === 'approved' ? 'bold' : 'normal'
          }}
        >
          Approved
        </button>
        <button
          onClick={() => setFilterStatus('rejected')}
          style={{
            padding: '8px 16px',
            border: filterStatus === 'rejected' ? '2px solid #dc3545' : '1px solid #ccc',
            backgroundColor: filterStatus === 'rejected' ? '#dc3545' : '#fff',
            color: filterStatus === 'rejected' ? '#fff' : '#000',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: filterStatus === 'rejected' ? 'bold' : 'normal'
          }}
        >
          Rejected
        </button>
        <button
          onClick={() => setFilterStatus('')}
          style={{
            padding: '8px 16px',
            border: filterStatus === '' ? '2px solid #6c757d' : '1px solid #ccc',
            backgroundColor: filterStatus === '' ? '#6c757d' : '#fff',
            color: filterStatus === '' ? '#fff' : '#000',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: filterStatus === '' ? 'bold' : 'normal'
          }}
        >
          All
        </button>
      </div>

      {error && (
        <div style={{
          padding: '15px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: '#fff' }}>Loading registrations...</div>
      ) : registrations.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          <p>No {filterStatus} registration requests</p>
          {!error && <p style={{ fontSize: '14px', marginTop: '10px' }}>Devices will appear here when they request access to the fleet.</p>}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: '#000'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#2a2a2a' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #333', color: '#fff', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #333', color: '#fff', fontWeight: 600 }}>Device Label</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #333', color: '#fff', fontWeight: 600 }}>Device ID</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #333', color: '#fff', fontWeight: 600 }}>Model</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #333', color: '#fff', fontWeight: 600 }}>Manufacturer</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #333', color: '#fff', fontWeight: 600 }}>OS Version</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #333', color: '#fff', fontWeight: 600 }}>Request IP</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #333', color: '#fff', fontWeight: 600 }}>Requested</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #333', color: '#fff', fontWeight: 600 }}>Last Polled</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #333', color: '#fff', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {registrations.map((reg, index) => {
                const isEvenRow = index % 2 === 0;
                return (
                  <tr key={reg._id} style={{ backgroundColor: isEvenRow ? '#1a1a1a' : '#000' }}>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: 'bold' }}>{getStatusBadge(reg.status)}</td>
                    <td style={{ padding: '12px', fontWeight: 'bold', color: '#fff' }}>
                      {reg.requestedDescription || 'No description'}
                    </td>
                    <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px', color: '#fff', fontWeight: 'bold' }}>
                      {reg.deviceId}
                    </td>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: 'bold' }}>{reg.deviceInfo?.model || 'Unknown'}</td>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: 'bold' }}>{reg.deviceInfo?.manufacturer || 'Unknown'}</td>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: 'bold' }}>{reg.deviceInfo?.osVersion || 'Unknown'}</td>
                    <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px', color: '#fff', fontWeight: 'bold' }}>
                      {reg.requestIp || 'Unknown'}
                    </td>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: 'bold' }}>{formatTimestamp(reg.createdAt)}</td>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: 'bold' }}>{formatTimestamp(reg.lastPolledAt)}</td>
                    <td style={{ padding: '12px' }}>
                    {reg.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleEdit(reg)}
                          style={{
                            padding: '4px 12px',
                            marginRight: '5px',
                            marginBottom: '5px',
                            backgroundColor: '#007bff',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          ✏️ Edit Label
                        </button>
                        <button
                          onClick={() => handleApprove(reg)}
                          style={{
                            padding: '4px 12px',
                            marginRight: '5px',
                            marginBottom: '5px',
                            backgroundColor: '#28a745',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => handleReject(reg)}
                          style={{
                            padding: '4px 12px',
                            marginBottom: '5px',
                            backgroundColor: '#dc3545',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          ✗ Reject
                        </button>
                      </>
                    )}
                    {reg.status === 'approved' && (
                      <>
                        <button
                          onClick={() => handleEdit(reg)}
                          style={{
                            padding: '4px 12px',
                            marginRight: '5px',
                            marginBottom: '5px',
                            backgroundColor: '#007bff',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          ✏️ Edit Label
                        </button>
                        <button
                          onClick={() => handleDelete(reg._id)}
                          style={{
                            padding: '4px 12px',
                            backgroundColor: '#6c757d',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                    {reg.status === 'rejected' && (
                      <button
                        onClick={() => handleDelete(reg._id)}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: '#6c757d',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Approve Dialog */}
      {approveDialog.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3>Approve Device Registration</h3>
            <p>Are you sure you want to approve this device?</p>
            <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <div><strong>Device ID:</strong> {approveDialog.registration?.deviceId}</div>
              <div><strong>Model:</strong> {approveDialog.registration?.deviceInfo?.model}</div>
              <div><strong>Manufacturer:</strong> {approveDialog.registration?.deviceInfo?.manufacturer}</div>
              <div><strong>OS:</strong> {approveDialog.registration?.deviceInfo?.osVersion}</div>
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setApproveDialog({ open: false, registration: null })}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmApprove}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ✓ Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      {rejectDialog.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3>Reject Device Registration</h3>
            <p>Please provide a reason for rejecting this device:</p>
            <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <div><strong>Device ID:</strong> {rejectDialog.registration?.deviceId}</div>
              <div><strong>Model:</strong> {rejectDialog.registration?.deviceInfo?.model}</div>
            </div>
            <textarea
              value={rejectDialog.reason}
              onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
              placeholder="Enter rejection reason..."
              style={{
                width: '100%',
                marginTop: '15px',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                minHeight: '80px',
                fontFamily: 'inherit'
              }}
            />
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRejectDialog({ open: false, registration: null, reason: '' })}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ✗ Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Label Dialog */}
      {editDialog.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%'
          }}>
            <h3>Edit Device Label</h3>
            <p>Customize the device label before approving. This label will be used to identify the device in the fleet management system.</p>
            <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <div><strong>Device ID:</strong> {editDialog.registration?.deviceId}</div>
              <div><strong>Model:</strong> {editDialog.registration?.deviceInfo?.model}</div>
              <div><strong>Manufacturer:</strong> {editDialog.registration?.deviceInfo?.manufacturer}</div>
            </div>
            <div style={{ marginTop: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Device Label:
              </label>
              <input
                type="text"
                value={editDialog.description}
                onChange={(e) => setEditDialog({ ...editDialog, description: e.target.value })}
                placeholder="e.g., LOS Terminal 1 - Departures Display - Gate 5"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
                autoFocus
              />
              <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                Tip: Use a descriptive name that includes location and purpose (e.g., "Terminal 2 - Gate 10 - Arrivals")
              </p>
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditDialog({ open: false, registration: null, description: '' })}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmEdit}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ✓ Save Label
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
